import { useEffect, useMemo, useState } from "react";
import { Check, Loader, MessageCircle, Search, Send, Share2, X } from "lucide-react";
import { createConversation, searchUsers, sendMessage, type SearchUserResult } from "../api/messages";
import type { Post } from "../types";
import { formatBlackspaceDateTime } from "../utils/time";

type PostShareModalProps = {
  post: Post;
  open: boolean;
  onClose: () => void;
};

function currentUsername() {
  try {
    const raw = localStorage.getItem("user");
    if (raw) return String(JSON.parse(raw)?.username || "").toLowerCase();
  } catch {}
  return "";
}

function buildSharedPostMessage(post: Post, note: string) {
  const author = post.author?.displayName || post.author?.username || "Anonymous";
  const username = post.author?.username ? `@${post.author.username}` : "anonymous";
  const space = post.space?.name || "Blackspace";
  const created = post.createdAt ? formatBlackspaceDateTime(post.createdAt) : "";
  const preview = String(post.content || "").trim();
  const origin = window.location.origin;
  const lines = [
    note.trim() ? note.trim() : "",
    "Shared a Blackspace post",
    `${author} (${username}) in ${space}${created ? ` at ${created}` : ""}`,
    "",
    preview,
    "",
    `Post reference: ${origin}/post/${encodeURIComponent(String(post.id))}`,
  ].filter((line, index) => index !== 0 || Boolean(line));
  return lines.join("\n").slice(0, 4000);
}

export default function PostShareModal({ post, open, onClose }: PostShareModalProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchUserResult[]>([]);
  const [selected, setSelected] = useState<SearchUserResult | null>(null);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState<"idle" | "sent" | "error">("idle");
  const [error, setError] = useState("");
  const me = useMemo(currentUsername, []);

  useEffect(() => {
    if (!open) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [onClose, open]);

  useEffect(() => {
    if (!open) return;
    const normalized = query.trim().replace(/^@+/, "");
    setStatus("idle");
    setError("");
    if (normalized.length < 2) {
      setResults([]);
      return;
    }

    let alive = true;
    setLoading(true);
    const timer = window.setTimeout(() => {
      searchUsers(normalized)
        .then((users) => {
          if (!alive) return;
          setResults(users.filter((user) => user.username.toLowerCase() !== me));
        })
        .catch(() => {
          if (alive) setResults([]);
        })
        .finally(() => {
          if (alive) setLoading(false);
        });
    }, 220);

    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [me, open, query]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults([]);
      setSelected(null);
      setNote("");
      setLoading(false);
      setSending(false);
      setStatus("idle");
      setError("");
    }
  }, [open]);

  if (!open) return null;

  const sharePost = async () => {
    if (!selected || sending) return;
    setSending(true);
    setStatus("idle");
    setError("");
    try {
      const conversation = await createConversation(selected.id);
      await sendMessage(conversation.id, buildSharedPostMessage(post, note), "text", `share-post-${post.id}-${Date.now()}`);
      setStatus("sent");
      window.setTimeout(onClose, 900);
    } catch (err: any) {
      setError(err?.message || "Could not share this post.");
      setStatus("error");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/65 px-4 backdrop-blur-md" onMouseDown={onClose}>
      <div
        className="w-full max-w-lg overflow-hidden rounded-xl border border-cyan-300/20 bg-neutral-950/85 text-white shadow-[0_0_70px_rgba(34,211,238,0.18)]"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="border-b border-neutral-800 bg-[radial-gradient(circle_at_12%_0%,rgba(34,211,238,0.16),transparent_30%),radial-gradient(circle_at_90%_0%,rgba(168,85,247,0.14),transparent_28%)] p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.24em] text-cyan-200">Share</p>
              <h2 className="mt-2 text-2xl font-black">Send post to a user</h2>
              <p className="mt-1 text-sm text-neutral-400">This creates a real message in their chat.</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="grid h-10 w-10 place-items-center rounded-lg border border-neutral-700 bg-neutral-900/80 text-neutral-300 transition hover:border-cyan-300/40 hover:text-white"
              aria-label="Close share post"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="space-y-4 p-5">
          <div className="rounded-lg border border-neutral-800 bg-black/40 p-3">
            <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.16em] text-neutral-500">
              <Share2 size={14} className="text-cyan-300" />
              Post preview
            </div>
            <p className="mt-2 line-clamp-3 text-sm leading-6 text-neutral-200">{post.content}</p>
          </div>

          <label className="block">
            <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-neutral-500">Recipient</span>
            <div className="flex items-center gap-2 rounded-lg border border-neutral-800 bg-black/50 px-3 py-2 focus-within:border-cyan-300/50">
              <Search size={16} className="text-neutral-500" />
              <input
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setSelected(null);
                }}
                placeholder="Search username..."
                className="min-w-0 flex-1 bg-transparent text-sm text-white outline-none placeholder:text-neutral-600"
              />
              {loading && <Loader size={15} className="animate-spin text-cyan-300" />}
            </div>
          </label>

          {(results.length > 0 || selected) && (
            <div className="stealth-scroll max-h-44 space-y-2 overflow-y-auto rounded-lg border border-neutral-800 bg-black/25 p-2">
              {selected && (
                <button
                  type="button"
                  className="flex w-full items-center gap-3 rounded-lg border border-emerald-300/30 bg-emerald-300/10 p-3 text-left"
                >
                  <span className="grid h-9 w-9 place-items-center rounded-lg border border-emerald-300/30 bg-black/30 text-sm font-black text-emerald-200">
                    {(selected.displayName || selected.username).slice(0, 1).toUpperCase()}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-bold text-white">{selected.displayName || selected.username}</span>
                    <span className="block truncate text-xs text-neutral-400">@{selected.username}</span>
                  </span>
                  <Check size={16} className="text-emerald-300" />
                </button>
              )}
              {!selected && results.map((user) => (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => {
                    setSelected(user);
                    setQuery(`@${user.username}`);
                    setResults([]);
                  }}
                  className="flex w-full items-center gap-3 rounded-lg border border-neutral-800 bg-neutral-950/80 p-3 text-left transition hover:border-cyan-300/35 hover:bg-neutral-900"
                >
                  {user.avatarUrl ? (
                    <img src={user.avatarUrl} alt="" className="h-9 w-9 rounded-lg border border-neutral-700 object-cover" />
                  ) : (
                    <span className="grid h-9 w-9 place-items-center rounded-lg border border-neutral-700 bg-neutral-900 text-sm font-black text-cyan-200">
                      {(user.displayName || user.username).slice(0, 1).toUpperCase()}
                    </span>
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-bold text-white">{user.displayName || user.username}</span>
                    <span className="block truncate text-xs text-neutral-500">@{user.username}</span>
                  </span>
                  <MessageCircle size={16} className="text-cyan-300" />
                </button>
              ))}
            </div>
          )}

          <label className="block">
            <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-neutral-500">Optional note</span>
            <textarea
              value={note}
              onChange={(event) => setNote(event.target.value.slice(0, 500))}
              rows={3}
              placeholder="Add a line before the shared post..."
              className="stealth-scroll w-full resize-none rounded-lg border border-neutral-800 bg-black/50 px-3 py-2 text-sm leading-6 text-white outline-none transition placeholder:text-neutral-600 focus:border-cyan-300/50"
            />
          </label>

          {error && <p className="rounded-lg border border-red-400/20 bg-red-500/10 p-3 text-sm text-red-200">{error}</p>}

          <div className="flex justify-end gap-2 border-t border-neutral-800 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-neutral-700 bg-neutral-900/70 px-4 py-2 text-sm font-semibold text-neutral-300 transition hover:border-neutral-500 hover:text-white"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={sharePost}
              disabled={!selected || sending || status === "sent"}
              className="inline-flex items-center gap-2 rounded-lg border border-cyan-300/30 bg-cyan-300/15 px-5 py-2 text-sm font-black text-cyan-100 shadow-[0_0_24px_rgba(34,211,238,0.1)] transition hover:border-cyan-200/60 hover:bg-cyan-300/20 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {status === "sent" ? <Check size={16} /> : sending ? <Loader size={16} className="animate-spin" /> : <Send size={16} />}
              {status === "sent" ? "Shared" : sending ? "Sending" : "Share"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
